/**
 * Índices en memoria sobre una instantánea `DatosCiclo` (cacheados por referencia).
 * Permiten consultas O(1) para cumplir RNF-03 con ~2.400 asistencias.
 */
import {
  AsistenciaEntity, DatosCiclo, GrupoEntity, HabilitacionEntity, InscripcionEntity, ModuloEntity,
  ParticipanteEntity, RolFormativoEntity, SesionEntity, UsuarioEntity
} from '../models/asistencia.model';

export interface IndiceDatos {
  grupos: Map<string, GrupoEntity>;
  sesiones: Map<string, SesionEntity>;
  modulos: Map<string, ModuloEntity>;
  roles: Map<string, RolFormativoEntity>;
  usuarios: Map<string, UsuarioEntity>;
  participantes: Map<string, ParticipanteEntity>;
  sesionesPorGrupo: Map<string, SesionEntity[]>;
  inscripcionActiva: Map<string, InscripcionEntity>;
  inscripcionesPorParticipante: Map<string, InscripcionEntity[]>;
  inscritosActivosPorGrupo: Map<string, InscripcionEntity[]>;
  asistenciasPorParticipante: Map<string, AsistenciaEntity[]>;
  asistenciasPorSesion: Map<string, AsistenciaEntity[]>;
  habilitacionPorParticipante: Map<string, HabilitacionEntity>;
}

const cache = new WeakMap<DatosCiclo, IndiceDatos>();

function agrupar<T>(items: T[], clave: (t: T) => string): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const it of items) {
    const k = clave(it);
    const lista = m.get(k);
    if (lista) lista.push(it); else m.set(k, [it]);
  }
  return m;
}

export function indice(datos: DatosCiclo): IndiceDatos {
  const existente = cache.get(datos);
  if (existente) return existente;

  const sesionesPorGrupo = agrupar(datos.sesiones, s => s.grupoId);
  sesionesPorGrupo.forEach(lista => lista.sort((a, b) => a.numero - b.numero));

  const habilitacionPorParticipante = new Map<string, HabilitacionEntity>();
  for (const h of datos.habilitaciones) {
    if (h.eliminado) continue;
    const previa = habilitacionPorParticipante.get(h.participanteId);
    if (!previa || previa.estado === 'RETIRADO' || h.creadoEn > previa.creadoEn) {
      habilitacionPorParticipante.set(h.participanteId, h);
    }
  }

  const inscripcionActiva = new Map<string, InscripcionEntity>();
  for (const i of datos.inscripciones) {
    if (i.estado !== 'ACTIVA') continue;
    const hab = habilitacionPorParticipante.get(i.participanteId);
    if (!hab || hab.estado !== 'INSCRITO' || hab.eliminado || hab.activo === false) continue;
    const previa = inscripcionActiva.get(i.participanteId);
    if (!previa || i.creadaEn >= previa.creadaEn) {
      inscripcionActiva.set(i.participanteId, i);
    }
  }
  const activasUnicas = Array.from(inscripcionActiva.values());

  const idx: IndiceDatos = {
    grupos: new Map(datos.grupos.map(g => [g.id, g])),
    sesiones: new Map(datos.sesiones.map(s => [s.id, s])),
    modulos: new Map(datos.modulos.map(m => [m.id, m])),
    roles: new Map(datos.roles.map(r => [r.id, r])),
    usuarios: new Map(datos.usuarios.map(u => [u.id, u])),
    participantes: new Map(datos.participantes.map(p => [p.id, p])),
    sesionesPorGrupo,
    inscripcionActiva,
    inscripcionesPorParticipante: agrupar(datos.inscripciones, i => i.participanteId),
    inscritosActivosPorGrupo: agrupar(activasUnicas, i => i.grupoId),
    asistenciasPorParticipante: agrupar(datos.asistencias, a => a.participanteId),
    asistenciasPorSesion: agrupar(datos.asistencias, a => a.sesionId),
    habilitacionPorParticipante
  };
  cache.set(datos, idx);
  return idx;
}

export function nombreCompleto(p: Pick<ParticipanteEntity, 'nombres' | 'apellidos'>): string {
  return `${p.nombres} ${p.apellidos}`.trim();
}

/** "Laura G." — formato corto usado en alertas para minimizar datos personales. */
export function nombreCorto(nombre: string): string {
  const partes = nombre.trim().split(/\s+/);
  if (partes.length < 2) return partes[0] ?? '';
  return `${partes[0]} ${partes[partes.length > 2 ? partes.length - 2 : 1].charAt(0)}.`;
}

export function documentoEnmascarado(documento: string): string {
  const limpio = (documento ?? '').trim();
  if (!limpio) return '—';
  return `••••${limpio.slice(-4)}`;
}

export function correoEnmascarado(correo: string): string {
  const limpio = (correo ?? '').trim().toLowerCase();
  if (!limpio) return '—';
  const partes = limpio.split('@');
  if (partes.length !== 2) return '••••';
  const [local, dominio] = partes;
  if (local.length <= 2) return `${local.charAt(0)}••••@${dominio}`;
  if (local.length <= 4) return `${local.charAt(0)}••••${local.slice(-1)}@${dominio}`;
  return `${local.slice(0, 2)}••••${local.slice(-2)}@${dominio}`;
}

export function limpiarDetalleMJ(detalle?: string | null): string {
  if (!detalle) return '';
  return detalle
    .replace(/\s*\((?:ID\s*#|MJ\s*#)\d+\)/gi, '')
    .replace(/\b(?:Usuario\s+)?MJ\s*#\d+\s*·?\s*/gi, '')
    .replace(/\bID\s*#\d+\b/gi, '')
    .replace(/\s*\(simulado\)/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Mentor responsable de una sesión: suplente/asignado o, en su defecto, titular del grupo. */
export function mentorDeSesion(datos: DatosCiclo, sesion: SesionEntity): UsuarioEntity | undefined {
  const idx = indice(datos);
  const g = idx.grupos.get(sesion.grupoId);
  const id = sesion.esSuplencia
    ? (sesion.mentorAsignadoId ?? g?.mentorTitularId)
    : (g?.mentorTitularId ?? (sesion.tomadaEn ? sesion.mentorAsignadoId : undefined));
  return id ? idx.usuarios.get(id) : undefined;
}

export function mentorTitularDeGrupo(datos: DatosCiclo, grupoId: string): UsuarioEntity | undefined {
  const idx = indice(datos);
  const id = idx.grupos.get(grupoId)?.mentorTitularId;
  return id ? idx.usuarios.get(id) : undefined;
}
